import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartHandshake, Loader2, CheckCircle2 } from "lucide-react";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const CARE_TYPES = ["Personal Care & Hygiene", "Meal Preparation", "Mobility & Fall Prevention", "Respite Care", "Nursing Supervision", "Companionship", "Not sure yet"];

export default function RequestCare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [caregiverName, setCaregiverName] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", care_type: "", message: "", caregiver_id: params.get("caregiver") || null });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const cg = params.get("caregiver");
    if (cg) api.get(`/caregivers/${cg}`).then((r) => setCaregiverName(r.data.name)).catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_type) { toast.error("Please select a type of care"); return; }
    setLoading(true);
    try {
      await api.post("/care-requests", form);
      setDone(true);
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-5" />
      <h1 className="font-display text-3xl font-bold text-blue-950 dark:text-blue-50">Request received!</h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">Thank you — our Columbus care team will reach out within one business day to build your care plan.</p>
      <Button onClick={() => navigate("/")} className="mt-7 bg-blue-900 text-white h-12 px-7">Back to home</Button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-blue-900 text-white mx-auto mb-4"><HeartHandshake className="w-7 h-7" /></span>
          <h1 className="font-display text-4xl font-bold text-blue-950 dark:text-blue-50">Request Care</h1>
          <p className="mt-2 text-slate-600 dark:text-slate-300">{caregiverName ? `Requesting care with ${caregiverName}.` : "Tell us about your needs and we'll match you with the right caregiver."}</p>
        </div>

        <form onSubmit={submit} data-testid="care-request-form" className="bg-white dark:bg-blue-900/30 rounded-2xl p-8 shadow-lg border border-blue-900/5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="r-name">Your name</Label><Input id="r-name" data-testid="care-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label htmlFor="r-phone">Phone</Label><Input id="r-phone" data-testid="care-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label htmlFor="r-email">Email</Label><Input id="r-email" type="email" data-testid="care-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label>Type of care</Label>
            <Select value={form.care_type} onValueChange={(v) => setForm({ ...form, care_type: v })}>
              <SelectTrigger data-testid="care-type-select" className="mt-1.5"><SelectValue placeholder="Select a service" /></SelectTrigger>
              <SelectContent>{CARE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="r-msg">Tell us more (optional)</Label><Textarea id="r-msg" data-testid="care-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" disabled={loading} data-testid="care-request-submit-button" className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Request"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
